import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Save, Image, Plus, Trash2, GripVertical, 
  Eye, EyeOff, Star, Sparkles, Tag, Layers,
  Upload, Link2, ChevronDown, ChevronUp,
  Package, DollarSign, Palette, Ruler
} from 'lucide-react';
import { adminApi } from '../../lib/api';
import { toast } from 'sonner';

// Tab Button Component
const TabButton = ({ active, onClick, children, icon: Icon }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-all ${
      active 
        ? 'border-vav-accent-cyan text-vav-accent-cyan' 
        : 'border-transparent text-vav-fg-muted hover:text-vav-fg'
    }`}
  >
    {Icon && <Icon className="w-4 h-4" />}
    {children}
  </button>
);

// Image Upload Card
const ImageUploadCard = ({ image, onRemove, onAdd, isPrimary }) => {
  if (!image) {
    return (
      <button
        onClick={onAdd}
        className="aspect-square rounded-xl border-2 border-dashed border-vav-border hover:border-vav-accent-cyan flex flex-col items-center justify-center gap-2 transition-colors bg-vav-bg-subtle/30"
      >
        <Upload className="w-6 h-6 text-vav-fg-muted" />
        <span className="text-xs text-vav-fg-muted">Görsel Ekle</span>
      </button>
    );
  }

  return (
    <div className="relative aspect-square rounded-xl overflow-hidden group border border-vav-border">
      <img src={image} alt="" className="w-full h-full object-cover" />
      {isPrimary && (
        <div className="absolute top-2 left-2 px-2 py-0.5 bg-vav-accent-cyan text-black text-xs rounded">
          Ana Görsel
        </div>
      )}
      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
        <button
          onClick={onRemove}
          className="p-2 rounded-full bg-red-500/80 text-white hover:bg-red-500"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// Variant Row Component - Enhanced with price and images
const VariantRow = ({ variant, index, onUpdate, onRemove, colors, sizes, onAddImage }) => {
  const [showImages, setShowImages] = useState(false);
  
  return (
    <div className="p-3 bg-vav-bg-subtle/30 rounded-lg space-y-2">
      <div className="grid grid-cols-12 gap-3 items-center">
        <div className="col-span-1 flex justify-center">
          <GripVertical className="w-4 h-4 text-vav-fg-dark cursor-move" />
        </div>
        <div className="col-span-2">
          <select
            value={variant.color}
            onChange={(e) => {
              const selectedColor = colors.find(c => c.name === e.target.value);
              onUpdate(index, 'color', e.target.value);
              if (selectedColor) {
                onUpdate(index, 'color_code', selectedColor.code);
              }
            }}
            className="w-full px-2 py-1.5 bg-vav-bg border border-vav-border rounded text-sm text-vav-fg focus:border-vav-accent-cyan focus:outline-none"
          >
            <option value="">Renk Seç</option>
            {colors.map(c => (
              <option key={c.color_id || c.name} value={c.name}>{c.name}</option>
            ))}
          </select>
        </div>
        <div className="col-span-2">
          <select
            value={variant.size}
            onChange={(e) => onUpdate(index, 'size', e.target.value)}
            className="w-full px-2 py-1.5 bg-vav-bg border border-vav-border rounded text-sm text-vav-fg focus:border-vav-accent-cyan focus:outline-none"
          >
            <option value="">Beden Seç</option>
            {sizes.map(s => (
              <option key={s.size_id || s.name} value={s.name}>{s.name}</option>
            ))}
          </select>
        </div>
        <div className="col-span-2">
          <input
            type="number"
            value={variant.price || ''}
            onChange={(e) => onUpdate(index, 'price', e.target.value ? parseFloat(e.target.value) : null)}
            placeholder="Fiyat (₺)"
            className="w-full px-2 py-1.5 bg-vav-bg border border-vav-border rounded text-sm text-vav-fg focus:border-vav-accent-cyan focus:outline-none"
          />
        </div>
        <div className="col-span-2">
          <input
            type="number"
            value={variant.stock}
            onChange={(e) => onUpdate(index, 'stock', parseInt(e.target.value) || 0)}
            placeholder="Stok"
            className="w-full px-2 py-1.5 bg-vav-bg border border-vav-border rounded text-sm text-vav-fg focus:border-vav-accent-cyan focus:outline-none"
          />
        </div>
        <div className="col-span-2">
          <input
            type="text"
            value={variant.sku}
            onChange={(e) => onUpdate(index, 'sku', e.target.value)}
            placeholder="SKU"
            className="w-full px-2 py-1.5 bg-vav-bg border border-vav-border rounded text-sm text-vav-fg focus:border-vav-accent-cyan focus:outline-none"
          />
        </div>
        <div className="col-span-1 flex items-center gap-1">
          <button 
            onClick={() => setShowImages(!showImages)} 
            className={`p-1 ${variant.images?.length > 0 ? 'text-vav-accent-cyan' : 'text-vav-fg-muted'} hover:text-vav-accent-cyan`}
            title="Görseller"
          >
            <Image className="w-4 h-4" />
          </button>
          <button onClick={() => onRemove(index)} className="p-1 text-red-400 hover:text-red-300">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      {/* Variant Images */}
      {showImages && (
        <div className="pl-8 pt-2 border-t border-vav-border/50">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs text-vav-fg-muted">Varyant Görselleri ({variant.images?.length || 0})</span>
            <div 
              className="w-4 h-4 rounded-full border border-vav-border"
              style={{ backgroundColor: variant.color_code || '#333' }}
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {(variant.images || []).map((img, imgIdx) => (
              <div key={imgIdx} className="relative w-16 h-16 rounded-lg overflow-hidden group">
                <img src={img} alt="" className="w-full h-full object-cover" />
                <button
                  onClick={() => {
                    const newImages = [...(variant.images || [])];
                    newImages.splice(imgIdx, 1);
                    onUpdate(index, 'images', newImages);
                  }}
                  className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100"
                >
                  <Trash2 className="w-4 h-4 text-red-400" />
                </button>
              </div>
            ))}
            <button
              onClick={() => {
                const url = prompt('Görsel URL\'si girin:');
                if (url) {
                  onUpdate(index, 'images', [...(variant.images || []), url]);
                }
              }}
              className="w-16 h-16 rounded-lg border border-dashed border-vav-border hover:border-vav-accent-cyan flex items-center justify-center"
            >
              <Plus className="w-4 h-4 text-vav-fg-muted" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Main Product Editor Component
const ProductEditor = ({ product, onClose, onSave }) => {
  const isEditing = !!product?.product_id;
  const [activeTab, setActiveTab] = useState('general');
  const [saving, setSaving] = useState(false);
  const [technologies, setTechnologies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [masterColors, setMasterColors] = useState([]);
  const [masterSizes, setMasterSizes] = useState([]);
  const [masterSpecFields, setMasterSpecFields] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    name_en: '',
    slug: '',
    description: '',
    description_en: '',
    categories: [],  // Multiple categories
    category: '',    // Primary category (backward compatible)
    subcategory: '',
    brand: 'VAVWEAR',
    price: '',
    original_price: '',
    discount_percent: '',
    images: [],
    videos: [],
    variants: [],
    specs: {},
    technologies: [],
    tags: [],
    is_active: true,
    is_featured: false,
    is_new: true,
    // SEO
    seo_title: '',
    seo_description: '',
    seo_keywords: [],
  });

  useEffect(() => {
    // Load all data including master data
    const loadData = async () => {
      try {
        const [techRes, catRes, colorsRes, sizesRes, specFieldsRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/technologies`),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/categories`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
          }),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/masterdata/colors`),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/masterdata/sizes`),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/masterdata/spec-fields`)
        ]);
        
        const techData = await techRes.json();
        const catData = await catRes.json();
        const colorsData = await colorsRes.json();
        const sizesData = await sizesRes.json();
        const specFieldsData = await specFieldsRes.json();
        
        setTechnologies(techData.technologies || []);
        setCategories(catData.categories || []);
        setMasterColors(colorsData.colors || []);
        setMasterSizes(sizesData.sizes || []);
        setMasterSpecFields(specFieldsData.fields || []);
      } catch (err) {
        console.error('Error loading data:', err);
      }
    };
    loadData();

    // Populate form if editing
    if (product && product.product_id) {
      setFormData({
        name: product.name || '',
        name_en: product.name_en || '',
        slug: product.slug || '',
        description: product.description || '',
        description_en: product.description_en || '',
        categories: product.categories || [],
        category: product.category || '',
        subcategory: product.subcategory || '',
        brand: product.brand || 'VAVWEAR',
        price: product.price || '',
        original_price: product.original_price || '',
        discount_percent: product.discount_percent || '',
        images: product.images || [],
        videos: product.videos || [],
        variants: product.variants || [],
        specs: product.specs || {},
        technologies: product.technologies || [],
        tags: product.tags || [],
        is_active: product.is_active !== false,
        is_featured: product.is_featured || false,
        is_new: product.is_new || false,
        seo_title: product.seo_title || '',
        seo_description: product.seo_description || '',
        seo_keywords: product.seo_keywords || [],
      });
    }
  }, [product]);

  // Toggle category selection (multi-select)
  const handleCategoryToggle = (categorySlug) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.includes(categorySlug)
        ? prev.categories.filter(c => c !== categorySlug)
        : [...prev.categories, categorySlug],
      // Set primary category if not set
      category: prev.category || categorySlug
    }));
  };

  // Auto-generate slug from name
  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/ğ/g, 'g').replace(/ü/g, 'u').replace(/ş/g, 's')
      .replace(/ı/g, 'i').replace(/ö/g, 'o').replace(/ç/g, 'c')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleNameChange = (value) => {
    setFormData(prev => ({
      ...prev,
      name: value,
      slug: prev.slug || generateSlug(value)
    }));
  };

  const handleAddImage = () => {
    const url = prompt('Görsel URL\'si girin:');
    if (url) {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, url]
      }));
    }
  };

  const handleRemoveImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleAddVariant = () => {
    const newVariant = {
      color: '',
      size: 'M',
      sku: `${formData.slug || 'SKU'}-${formData.variants.length + 1}`,
      stock: 10,
      color_code: '#333333'
    };
    setFormData(prev => ({
      ...prev,
      variants: [...prev.variants, newVariant]
    }));
  };

  const handleUpdateVariant = (index, field, value) => {
    setFormData(prev => {
      const newVariants = [...prev.variants];
      newVariants[index] = { ...newVariants[index], [field]: value };
      
      // Auto-set color code when color is selected
      if (field === 'color') {
        const color = masterColors.find(c => c.name === value);
        if (color) {
          newVariants[index].color_code = color.code;
        }
      }
      
      return { ...prev, variants: newVariants };
    });
  };

  const handleRemoveVariant = (index) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index)
    }));
  };

  const handleSpecChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      specs: { ...prev.specs, [key]: value }
    }));
  };

  const handleTechnologyToggle = (techSlug) => {
    setFormData(prev => ({
      ...prev,
      technologies: prev.technologies.includes(techSlug)
        ? prev.technologies.filter(t => t !== techSlug)
        : [...prev.technologies, techSlug]
    }));
  };

  const handleSave = async () => {
    // Validation
    if (!formData.name) {
      toast.error('Ürün adı zorunludur');
      return;
    }
    if (!formData.price) {
      toast.error('Fiyat zorunludur');
      return;
    }
    if (!formData.category) {
      toast.error('Kategori zorunludur');
      return;
    }
    if (formData.images.length === 0) {
      toast.error('En az bir görsel ekleyin');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price),
        original_price: formData.original_price ? parseFloat(formData.original_price) : null,
        discount_percent: formData.discount_percent ? parseInt(formData.discount_percent) : null,
      };

      if (isEditing) {
        await adminApi.updateProduct(product.product_id, payload);
        toast.success('Ürün güncellendi');
      } else {
        await adminApi.createProduct(payload);
        toast.success('Ürün oluşturuldu');
      }
      onSave();
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Kaydetme hatası: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 z-50 overflow-y-auto"
    >
      <div className="min-h-screen py-8 px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-6xl mx-auto bg-vav-bg-paper rounded-2xl border border-vav-border"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-vav-border">
            <div>
              <h2 className="font-heading text-2xl text-vav-fg">
                {isEditing ? 'Ürün Düzenle' : 'Yeni Ürün Oluştur'}
              </h2>
              <p className="text-sm text-vav-fg-muted mt-1">
                Ürün bilgilerini doldurun ve kaydedin
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-vav-fg-muted hover:text-vav-fg rounded-lg hover:bg-vav-bg-subtle transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-vav-border px-6 bg-vav-bg-subtle/30">
            <TabButton active={activeTab === 'general'} onClick={() => setActiveTab('general')} icon={Package}>
              Genel Bilgiler
            </TabButton>
            <TabButton active={activeTab === 'media'} onClick={() => setActiveTab('media')} icon={Image}>
              Görseller
            </TabButton>
            <TabButton active={activeTab === 'variants'} onClick={() => setActiveTab('variants')} icon={Layers}>
              Varyantlar
            </TabButton>
            <TabButton active={activeTab === 'pricing'} onClick={() => setActiveTab('pricing')} icon={DollarSign}>
              Fiyatlandırma
            </TabButton>
            <TabButton active={activeTab === 'specs'} onClick={() => setActiveTab('specs')} icon={Tag}>
              Özellikler
            </TabButton>
          </div>

          {/* Content */}
          <div className="p-6">
            <div className="grid grid-cols-3 gap-8">
              {/* Main Content - 2/3 */}
              <div className="col-span-2 space-y-6">
                {/* General Tab */}
                {activeTab === 'general' && (
                  <div className="space-y-6">
                    {/* Product Name */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-vav-fg mb-2">
                          Ürün Adı (TR) <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => handleNameChange(e.target.value)}
                          className="w-full px-4 py-3 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-accent-cyan focus:outline-none"
                          placeholder="Tactical Combat Pantolon"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-vav-fg mb-2">
                          Ürün Adı (EN)
                        </label>
                        <input
                          type="text"
                          value={formData.name_en}
                          onChange={(e) => setFormData({...formData, name_en: e.target.value})}
                          className="w-full px-4 py-3 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-accent-cyan focus:outline-none"
                          placeholder="Tactical Combat Pants"
                        />
                      </div>
                    </div>

                    {/* Slug */}
                    <div>
                      <label className="block text-sm font-medium text-vav-fg mb-2">
                        URL Slug <span className="text-red-400">*</span>
                      </label>
                      <div className="flex items-center gap-2">
                        <span className="text-vav-fg-muted text-sm">/product/</span>
                        <input
                          type="text"
                          value={formData.slug}
                          onChange={(e) => setFormData({...formData, slug: e.target.value})}
                          className="flex-1 px-4 py-3 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-accent-cyan focus:outline-none"
                          placeholder="tactical-combat-pantolon"
                        />
                      </div>
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-sm font-medium text-vav-fg mb-2">
                        Açıklama (TR)
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                        rows={4}
                        className="w-full px-4 py-3 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-accent-cyan focus:outline-none resize-none"
                        placeholder="Ürün açıklamasını yazın..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-vav-fg mb-2">
                        Açıklama (EN)
                      </label>
                      <textarea
                        value={formData.description_en}
                        onChange={(e) => setFormData({...formData, description_en: e.target.value})}
                        rows={4}
                        className="w-full px-4 py-3 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-accent-cyan focus:outline-none resize-none"
                        placeholder="Write product description..."
                      />
                    </div>

                    {/* Category & Subcategory */}
                    <div>
                      <label className="block text-sm font-medium text-vav-fg mb-2">
                        Kategoriler <span className="text-red-400">*</span>
                        <span className="text-xs text-vav-fg-muted ml-2">(Çoklu seçim)</span>
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {categories.map(cat => (
                          <button
                            key={cat.category_id || cat.slug}
                            type="button"
                            onClick={() => handleCategoryToggle(cat.slug || cat.name)}
                            className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                              formData.categories.includes(cat.slug || cat.name)
                                ? 'bg-vav-accent-cyan/20 border-vav-accent-cyan text-vav-accent-cyan'
                                : 'border-vav-border text-vav-fg-muted hover:border-vav-fg'
                            }`}
                          >
                            {cat.name}
                          </button>
                        ))}
                      </div>
                      {formData.categories.length > 0 && (
                        <p className="text-xs text-vav-fg-muted mt-2">
                          Ana kategori: <span className="text-vav-accent-cyan">{formData.category || formData.categories[0]}</span>
                        </p>
                      )}
                    </div>

                    {/* Technologies */}
                    <div>
                      <label className="block text-sm font-medium text-vav-fg mb-2">
                        Teknolojiler
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {technologies.map(tech => (
                          <button
                            key={tech.slug}
                            onClick={() => handleTechnologyToggle(tech.slug)}
                            className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                              formData.technologies.includes(tech.slug)
                                ? 'bg-vav-accent-cyan/20 border-vav-accent-cyan text-vav-accent-cyan'
                                : 'border-vav-border text-vav-fg-muted hover:border-vav-fg'
                            }`}
                          >
                            {tech.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Media Tab */}
                {activeTab === 'media' && (
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-vav-fg mb-4">
                        Ürün Görselleri <span className="text-red-400">*</span>
                      </label>
                      <div className="grid grid-cols-4 gap-4">
                        {formData.images.map((img, idx) => (
                          <ImageUploadCard
                            key={idx}
                            image={img}
                            isPrimary={idx === 0}
                            onRemove={() => handleRemoveImage(idx)}
                          />
                        ))}
                        <ImageUploadCard onAdd={handleAddImage} />
                      </div>
                      <p className="text-xs text-vav-fg-muted mt-2">
                        İlk görsel ana görsel olarak kullanılır. Görselleri sürükleyerek sıralayabilirsiniz.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-vav-fg mb-2">
                        Video URL (YouTube/Vimeo)
                      </label>
                      <input
                        type="text"
                        value={formData.videos[0] || ''}
                        onChange={(e) => setFormData({...formData, videos: e.target.value ? [e.target.value] : []})}
                        className="w-full px-4 py-3 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-accent-cyan focus:outline-none"
                        placeholder="https://youtube.com/watch?v=..."
                      />
                    </div>
                  </div>
                )}

                {/* Variants Tab */}
                {activeTab === 'variants' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-medium text-vav-fg">Ürün Varyantları</h3>
                        <p className="text-sm text-vav-fg-muted">Beden ve renk kombinasyonlarını ekleyin</p>
                      </div>
                      <button
                        onClick={handleAddVariant}
                        className="tactical-btn flex items-center gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        Varyant Ekle
                      </button>
                    </div>

                    {/* Variant Headers */}
                    {formData.variants.length > 0 && (
                      <div className="grid grid-cols-12 gap-3 px-3 text-xs text-vav-fg-muted uppercase">
                        <div className="col-span-1"></div>
                        <div className="col-span-2">Renk</div>
                        <div className="col-span-2">Beden</div>
                        <div className="col-span-2">SKU</div>
                        <div className="col-span-2">Stok</div>
                        <div className="col-span-2">Renk Kodu</div>
                        <div className="col-span-1"></div>
                      </div>
                    )}

                    {/* Variant Rows */}
                    <div className="space-y-2">
                      {formData.variants.map((variant, idx) => (
                        <VariantRow
                          key={idx}
                          variant={variant}
                          index={idx}
                          colors={masterColors.length > 0 ? masterColors : []}
                          sizes={masterSizes.filter(s => s.category === 'clothing')}
                          onUpdate={handleUpdateVariant}
                          onRemove={handleRemoveVariant}
                        />
                      ))}
                    </div>

                    {formData.variants.length === 0 && (
                      <div className="p-8 text-center text-vav-fg-muted bg-vav-bg-subtle/30 rounded-xl border border-dashed border-vav-border">
                        <Layers className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>Henüz varyant eklenmedi</p>
                        <p className="text-xs mt-1">Farklı beden ve renk kombinasyonları ekleyin</p>
                      </div>
                    )}

                    {/* Quick Add */}
                    <div className="p-4 bg-vav-bg-subtle/30 rounded-lg border border-vav-border">
                      <h4 className="text-sm font-medium text-vav-fg mb-3">Kayıtlı Seçenekler</h4>
                      <div className="flex flex-wrap gap-2 mb-3">
                        <span className="text-xs text-vav-fg-muted">Renkler:</span>
                        {masterColors.map(c => (
                          <div
                            key={c.color_id}
                            className="w-6 h-6 rounded-full border border-vav-border cursor-pointer hover:scale-110 transition-transform"
                            style={{ backgroundColor: c.code }}
                            title={c.name}
                          />
                        ))}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <span className="text-xs text-vav-fg-muted">Bedenler:</span>
                        {masterSizes.filter(s => s.category === 'clothing').map(s => (
                          <span key={s.size_id} className="px-2 py-0.5 bg-vav-bg rounded text-xs text-vav-fg">{s.name}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Pricing Tab */}
                {activeTab === 'pricing' && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-vav-fg mb-2">
                          Satış Fiyatı (₺) <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="number"
                          value={formData.price}
                          onChange={(e) => setFormData({...formData, price: e.target.value})}
                          className="w-full px-4 py-3 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-accent-cyan focus:outline-none"
                          placeholder="999"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-vav-fg mb-2">
                          Karşılaştırma Fiyatı (₺)
                        </label>
                        <input
                          type="number"
                          value={formData.original_price}
                          onChange={(e) => setFormData({...formData, original_price: e.target.value})}
                          className="w-full px-4 py-3 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-accent-cyan focus:outline-none"
                          placeholder="1299"
                        />
                        <p className="text-xs text-vav-fg-muted mt-1">Üstü çizili olarak gösterilir</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-vav-fg mb-2">
                          İndirim Yüzdesi (%)
                        </label>
                        <input
                          type="number"
                          value={formData.discount_percent}
                          onChange={(e) => setFormData({...formData, discount_percent: e.target.value})}
                          className="w-full px-4 py-3 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-accent-cyan focus:outline-none"
                          placeholder="20"
                          max={99}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-vav-fg mb-2">
                          Marka
                        </label>
                        <input
                          type="text"
                          value={formData.brand}
                          onChange={(e) => setFormData({...formData, brand: e.target.value})}
                          className="w-full px-4 py-3 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-accent-cyan focus:outline-none"
                          placeholder="VAVWEAR"
                        />
                      </div>
                    </div>

                    {/* Price Preview */}
                    {formData.price && (
                      <div className="p-4 bg-vav-bg-subtle/30 rounded-lg border border-vav-border">
                        <h4 className="text-sm font-medium text-vav-fg mb-2">Fiyat Önizleme</h4>
                        <div className="flex items-baseline gap-3">
                          <span className="text-2xl font-heading text-vav-fg">₺{formData.price}</span>
                          {formData.original_price && (
                            <span className="text-lg text-vav-fg-muted line-through">₺{formData.original_price}</span>
                          )}
                          {formData.discount_percent && (
                            <span className="px-2 py-0.5 bg-vav-accent-orange/20 text-vav-accent-orange text-sm rounded">
                              %{formData.discount_percent} İndirim
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Specs Tab */}
                {activeTab === 'specs' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-medium text-vav-fg mb-4">Teknik Özellikler</h3>
                      <p className="text-xs text-vav-fg-muted mb-4">
                        Sadece doldurduğunuz alanlar ürün sayfasında gösterilecektir.
                      </p>
                      <div className="space-y-3">
                        {masterSpecFields.map(field => (
                          <div key={field.field_id} className="grid grid-cols-3 gap-4 items-center">
                            <label className="text-sm text-vav-fg-muted flex items-center gap-2">
                              {field.name}
                              {field.unit && <span className="text-xs text-vav-fg-dark">({field.unit})</span>}
                            </label>
                            {field.field_type === 'select' ? (
                              <select
                                value={formData.specs[field.name] || ''}
                                onChange={(e) => handleSpecChange(field.name, e.target.value)}
                                className="col-span-2 px-4 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-accent-cyan focus:outline-none"
                              >
                                <option value="">Seçin...</option>
                                {(field.options || []).map(opt => (
                                  <option key={opt} value={opt}>{opt}</option>
                                ))}
                              </select>
                            ) : field.field_type === 'boolean' ? (
                              <div className="col-span-2">
                                <button
                                  type="button"
                                  onClick={() => handleSpecChange(field.name, formData.specs[field.name] === 'Evet' ? '' : 'Evet')}
                                  className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                                    formData.specs[field.name] === 'Evet'
                                      ? 'bg-vav-accent-green/20 text-vav-accent-green border border-vav-accent-green'
                                      : 'bg-vav-bg border border-vav-border text-vav-fg-muted'
                                  }`}
                                >
                                  {formData.specs[field.name] === 'Evet' ? 'Evet' : 'Hayır'}
                                </button>
                              </div>
                            ) : (
                              <input
                                type={field.field_type === 'number' ? 'number' : 'text'}
                                value={formData.specs[field.name] || ''}
                                onChange={(e) => handleSpecChange(field.name, e.target.value)}
                                className="col-span-2 px-4 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-accent-cyan focus:outline-none"
                                placeholder={`${field.name} girin...`}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* SEO Section */}
                    <div className="pt-4 border-t border-vav-border">
                      <h3 className="text-lg font-medium text-vav-fg mb-4 flex items-center gap-2">
                        SEO Ayarları
                      </h3>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm text-vav-fg-muted mb-1">SEO Başlığı</label>
                          <input
                            type="text"
                            value={formData.seo_title}
                            onChange={(e) => setFormData({...formData, seo_title: e.target.value})}
                            className="w-full px-4 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-accent-cyan focus:outline-none"
                            placeholder="Ürün adı | VAV Wear"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-vav-fg-muted mb-1">Meta Açıklama</label>
                          <textarea
                            value={formData.seo_description}
                            onChange={(e) => setFormData({...formData, seo_description: e.target.value})}
                            rows={2}
                            className="w-full px-4 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-accent-cyan focus:outline-none resize-none"
                            placeholder="Google arama sonuçlarında görünecek açıklama..."
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-vav-fg-muted mb-1">Anahtar Kelimeler</label>
                          <input
                            type="text"
                            value={(formData.seo_keywords || []).join(', ')}
                            onChange={(e) => setFormData({...formData, seo_keywords: e.target.value.split(',').map(t => t.trim()).filter(Boolean)})}
                            className="w-full px-4 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-accent-cyan focus:outline-none"
                            placeholder="tactical, pantolon, outdoor..."
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-vav-fg mb-2">
                        Etiketler (virgülle ayırın)
                      </label>
                      <input
                        type="text"
                        value={formData.tags.join(', ')}
                        onChange={(e) => setFormData({...formData, tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean)})}
                        className="w-full px-4 py-3 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-accent-cyan focus:outline-none"
                        placeholder="outdoor, tactical, waterproof"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Sidebar - 1/3 */}
              <div className="space-y-6">
                {/* Status Card */}
                <div className="p-4 bg-vav-bg-subtle/30 rounded-xl border border-vav-border">
                  <h4 className="text-sm font-medium text-vav-fg mb-4">Ürün Durumu</h4>
                  
                  <div className="space-y-3">
                    <label className="flex items-center justify-between cursor-pointer">
                      <span className="text-sm text-vav-fg-muted">Aktif</span>
                      <button
                        onClick={() => setFormData({...formData, is_active: !formData.is_active})}
                        className={`w-12 h-6 rounded-full transition-colors ${
                          formData.is_active ? 'bg-vav-accent-green' : 'bg-vav-bg-subtle'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${
                          formData.is_active ? 'translate-x-6' : 'translate-x-0.5'
                        }`} />
                      </button>
                    </label>

                    <label className="flex items-center justify-between cursor-pointer">
                      <span className="text-sm text-vav-fg-muted flex items-center gap-1">
                        <Star className="w-4 h-4" /> Öne Çıkan
                      </span>
                      <button
                        onClick={() => setFormData({...formData, is_featured: !formData.is_featured})}
                        className={`w-12 h-6 rounded-full transition-colors ${
                          formData.is_featured ? 'bg-vav-gold' : 'bg-vav-bg-subtle'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${
                          formData.is_featured ? 'translate-x-6' : 'translate-x-0.5'
                        }`} />
                      </button>
                    </label>

                    <label className="flex items-center justify-between cursor-pointer">
                      <span className="text-sm text-vav-fg-muted flex items-center gap-1">
                        <Sparkles className="w-4 h-4" /> Yeni Ürün
                      </span>
                      <button
                        onClick={() => setFormData({...formData, is_new: !formData.is_new})}
                        className={`w-12 h-6 rounded-full transition-colors ${
                          formData.is_new ? 'bg-vav-accent-cyan' : 'bg-vav-bg-subtle'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${
                          formData.is_new ? 'translate-x-6' : 'translate-x-0.5'
                        }`} />
                      </button>
                    </label>
                  </div>
                </div>

                {/* Preview Card */}
                {formData.images[0] && (
                  <div className="p-4 bg-vav-bg-subtle/30 rounded-xl border border-vav-border">
                    <h4 className="text-sm font-medium text-vav-fg mb-4">Önizleme</h4>
                    <div className="aspect-[3/4] rounded-lg overflow-hidden mb-3">
                      <img 
                        src={formData.images[0]} 
                        alt="Preview" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <p className="text-sm font-medium text-vav-fg truncate">{formData.name || 'Ürün Adı'}</p>
                    <p className="text-xs text-vav-fg-muted">{formData.category || 'Kategori'}</p>
                    {formData.price && (
                      <p className="text-lg font-heading text-vav-fg mt-2">₺{formData.price}</p>
                    )}
                  </div>
                )}

                {/* Summary */}
                <div className="p-4 bg-vav-bg-subtle/30 rounded-xl border border-vav-border">
                  <h4 className="text-sm font-medium text-vav-fg mb-3">Özet</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-vav-fg-muted">Görseller</span>
                      <span className="text-vav-fg">{formData.images.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-vav-fg-muted">Varyantlar</span>
                      <span className="text-vav-fg">{formData.variants.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-vav-fg-muted">Teknolojiler</span>
                      <span className="text-vav-fg">{formData.technologies.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-vav-fg-muted">Toplam Stok</span>
                      <span className="text-vav-fg">
                        {formData.variants.reduce((sum, v) => sum + (v.stock || 0), 0)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-vav-border bg-vav-bg-subtle/30">
            <button
              onClick={onClose}
              className="px-6 py-2 text-vav-fg-muted hover:text-vav-fg transition-colors"
            >
              İptal
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="tactical-btn flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Kaydediliyor...' : (isEditing ? 'Güncelle' : 'Ürün Oluştur')}
            </button>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default ProductEditor;
